import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Layout, Row, Col, Card, Typography, message, Form, Modal } from "antd";
import { selectAppPermissions } from "../../../store/auth/selectors";
import { getEntregas, deleteEntrega, updateEntrega } from "../../../services/financiero/finEntregasService";
import { getParticipantes, getProductos, getMunicipios, getDepartamentos, getProyectos } from "../../../services/financiero/finOtrosService";
import EntregasHeader from "./EntregasHeader";
import EntregasCards from "./components/EntregasCards";
import EntregasFilters from "./components/EntregasFilters";
import EntregasTable from "./components/EntregasTable";
import EntregasModal from "./components/EntregasModal";
import EntregaForm from "./components/EntregaForm";
const { Content } = Layout;
const { Text } = Typography;

export default function EntregasPage() {

  const appName = "entregas_financiero";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username || "system";
  const permissionAccess = permissions?.priv_access === "Y";
  const permissionDelete = permissions?.priv_delete === "Y";
  const permissionUpdate = permissions?.priv_update === "Y";
  const permissionInsert = permissions?.priv_insert === "Y";
  const permissionExport = permissions?.priv_export === "Y";

  const [loading, setLoading] = useState(false);
  const [entregas, setEntregas] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [editingEntrega, setEditingEntrega] = useState(null);
  const [form] = Form.useForm();

  const [filters, setFilters] = useState({
    search: "",
    proyecto: [],
    departamento: null,
    municipio: null,
    producto: null,
    tipo_entrega: null
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const cargarEntregas = async () => {
    try {
      setLoading(true);
      const data = await getEntregas();
      setEntregas(data || []);
    } catch (error) {
      console.error(error);
      message.error("Error cargando entregas");
    } finally {
      setLoading(false);
    }
  };

  const cargarCatalogos = async () => {
    try {
      const [
        participantesData,
        productosData,
        municipiosData,
        departamentosData,
        proyectosData
      ] = await Promise.all([
        getParticipantes(),
        getProductos(),
        getMunicipios(),
        getDepartamentos(),
        getProyectos()
      ]);
      setParticipantes(participantesData || []);
      setProductos(productosData || []);
      setMunicipios(municipiosData || []);
      setDepartamentos(departamentosData || []);
      setProyectos(proyectosData || []);
    } catch (error) {

      console.error(error);
      message.error("Error cargando catálogos");
    }
  };

  useEffect(() => {
    if (permissionAccess) {
      cargarEntregas();
      cargarCatalogos();
    }
  }, [permissionAccess]);

  const dataFiltrada = useMemo(() => {
    return entregas.filter((item) => {
      const texto = `
  ${item.identificacion || ""}
  ${item.nombre_entrega || ""}
  ${item.nombre_producto || ""}
`.toLowerCase();
      const matchSearch =
        !filters.search ||
        texto.includes(filters.search.toLowerCase());
      const matchProyecto =
  !filters.proyecto || filters.proyecto.length === 0 ||
  filters.proyecto.map(Number).includes(Number(item.oid_proyecto));
      const matchMunicipio =
        !filters.municipio ||
        Number(item.oid_municipio) === Number(filters.municipio);
      const matchProducto =
        !filters.producto ||
        Number(item.oid_producto) === Number(filters.producto);
      const matchTipo =
        !filters.tipo_entrega ||
        Number(item.tipo_entrega) === Number(filters.tipo_entrega);
      const matchDepartamento =
        !filters.departamento ||
        Number(item.oid_depto) === Number(filters.departamento);
      return (
        matchSearch &&
        matchProyecto &&
        matchMunicipio &&
        matchProducto &&
        matchTipo &&
        matchDepartamento
      );
    });
  }, [entregas, filters]);

  const filtrosCatalogos = useMemo(() => {

    const proyectosIds = new Set(entregas.map(e => e.oid_proyecto));
    const municipiosIds = new Set(entregas.map(e => e.oid_municipio));
    const deptosIds = new Set(entregas.map(e => e.oid_depto));
    const productosIds = new Set(entregas.map(e => e.oid_producto));

    return {
      proyectos: proyectos.filter(p => proyectosIds.has(p.oid)),
      municipios: municipios.filter(m => municipiosIds.has(m.oid)),
      departamentos: departamentos.filter(d => deptosIds.has(d.oid)),
      productos: productos.filter(p => productosIds.has(p.oid))
    };

  }, [entregas, proyectos, municipios, departamentos, productos]);

  const handleDelete = async (record) => {
    try {
      await deleteEntrega(record.oid, userLogin);
      message.success("Entrega eliminada");
      cargarEntregas();
    } catch (error) {
      console.error(error);
      message.error("Error eliminando");
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRows.length) {
      message.warning("Seleccione registros");
      return;
    }
    try {
      await Promise.all(
        selectedRows.map((row) =>
          deleteEntrega(row.oid, userLogin)
        )
      );
      message.success("Registros eliminados");
      setSelectedRows([]);
      cargarEntregas();
    } catch (error) {
      console.error(error);
      message.error("Error eliminando registros");
    }
  };

  const handleEdit = (record) => {
    setEditingEntrega(record);
  };


  const handleUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        carpeta: values.carpeta,
        nombre_archivo: values.nombre_archivo,
        fecha_entrega: values.fecha_entrega
          ? values.fecha_entrega.format("YYYY-MM-DD")
          : null
      };
      await updateEntrega(editingEntrega.oid, payload);
      message.success("Entrega actualizada");
      setEditingEntrega(null);
      form.resetFields();
      cargarEntregas();
    } catch (error) {
      console.error(error);
      message.error("Error actualizando entrega");
    }
  };

  if (!permissionAccess) {
    return (
      <Content style={{ padding: 24 }}>
        <Card>
          <Text>No tienes permisos para acceder a este módulo.</Text>
        </Card>
      </Content>
    );
  }

  return (
    <div className="container">
      <Content style={{ padding: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <EntregasHeader
              onCreate={() => setModalOpen(true)}
              onRefresh={cargarEntregas}
              onDeleteSelected={handleDeleteSelected}
              permissionInsert={permissionInsert}
              permissionDelete={permissionDelete}
              loading={loading}
              selectedRows={selectedRows}
            />
          </Col>
          <Col span={24}>
            <EntregasCards entregas={dataFiltrada} />
          </Col>
          <Col span={24}>
            <EntregasFilters
              filters={filters}
              setFilters={setFilters}
              departamentos={filtrosCatalogos.departamentos}
              municipios={filtrosCatalogos.municipios}
              proyectos={filtrosCatalogos.proyectos}
              productos={filtrosCatalogos.productos}
              data={dataFiltrada}
              userLogin={userLogin}
            />
          </Col>
          <Col span={24}>
            <EntregasTable
              data={dataFiltrada}
              loading={loading}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              onDelete={handleDelete}
              onEdit={handleEdit}
              permissionDelete={permissionDelete}
              permissionUpdate={permissionUpdate}
            />
          </Col>
          {editingEntrega && (
            <Col span={24}>
              <Modal
                title="Editar entrega"
                open={!!editingEntrega}
                onCancel={() => {
                  setEditingEntrega(null);
                  form.resetFields();
                }}
                onOk={() => form.submit()}
                okText="Actualizar"
                cancelText="Cancelar"
                width={1000}
                okButtonProps={{
                  disabled:
                    !form.isFieldsTouched(true) ||
                    form.getFieldsError().some(({ errors }) => errors.length)
                }}
              >
                <EntregaForm
                  form={form}
                  initialValues={editingEntrega}
                  participantes={participantes}
                  productos={productos}
                  municipios={municipios}
                  departamentos={departamentos}
                  proyectos={proyectos}
                  onFinish={handleUpdate}
                />
              </Modal>
            </Col>
          )}
        </Row>
        <EntregasModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          participantes={participantes}
          productos={productos}
          municipios={municipios}
          departamentos={departamentos}
          proyectos={proyectos}
          userLogin={userLogin}
          reload={cargarEntregas}
        />
      </Content>
    </div>
  );
}